import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ExpenseStatusControl } from 'molecules';

import { TCalendarRow, IncomeStateChip, fmtMoney } from '../shared';
import { RowActionsMenu } from './RowActionsMenu';

export interface IAgendaRowProps {
  /** The payment row to render. */
  row: TCalendarRow;
  /** Open the row in its edit surface. */
  onEdit: (row: TCalendarRow) => void;
  /** Delete a deletable row. */
  onDelete: (row: TCalendarRow) => void;
  /** Called after a successful reschedule (refetch). */
  onMoved?: () => void;
  /** Mark an unpaid expense row as paid. */
  onMarkPaid: (row: TCalendarRow) => void;
  /** Set an expense row to any status (inline chip-menu). */
  onSetStatus: (row: TCalendarRow, status: string) => void;
}

/**
 * One agenda line: контрагент (bold; falls back to the row title) — документ
 * (invoice number, or the title demoted to the secondary line) — сумма — chip
 * (income state / interactive expense status) — the shared «⋯» actions menu.
 */
export const AgendaRow = memo<IAgendaRowProps>(
  ({ row, onEdit, onDelete, onMoved, onMarkPaid, onSetStatus }) => {
    const { t } = useTranslation();

    const primary = row.counterpartyName || row.title || '—';
    // Secondary line: the document number when we have it; otherwise the title
    // (only when the counterparty already occupies the primary line).
    const secondary =
      row.invoiceNumber ||
      (row.counterpartyName && row.title !== row.counterpartyName
        ? row.title
        : null);

    return (
      <Line>
        <Names>
          <span className="ar-primary" title={primary}>
            {primary}
          </span>
          {secondary ? (
            <span className="ar-secondary" title={secondary}>
              {secondary}
            </span>
          ) : null}
        </Names>
        <Amount>{fmtMoney(row.amountPln)}</Amount>
        {row.direction === 'income' ? (
          <IncomeStateChip state={row.incomeState}>
            {t(`pages.paymentCalendar.incomeStates.${row.incomeState}`)}
          </IncomeStateChip>
        ) : (
          <ExpenseStatusControl
            value={row.status}
            onChange={(s) => onSetStatus(row, s)}
          />
        )}
        <RowActionsMenu
          row={row}
          onEdit={onEdit}
          onDelete={onDelete}
          onMoved={onMoved}
          onMarkPaid={onMarkPaid}
        />
      </Line>
    );
  },
);

/* ----------------------------- styles ----------------------------- */

const Line = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 12px;

  &:last-child {
    border-bottom: none;
  }
`;

const Names = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;

  .ar-primary {
    font-weight: 600;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ar-secondary {
    color: #94a3b8;
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Amount = styled.span`
  flex: 0 0 auto;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
